import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEdit, FaEye, FaPlus, FaSearch, FaTrash } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import DeleteConfirmDialog from "../../../components/common/DeleteConfirmDialog";
import {
  AskStrikeUpButton,
  Badge,
  Box,
  Button,
  Heading,
  HStack,
  IconButton,
  Input,
  RefreshButton,
  Table,
  Text,
  useDisclosure,
  VStack,
} from "../../../components/ui";
import {
  FeatureFlag,
  featureFlagService,
  ToolMapping,
} from "../../../services/featureFlagService";
import { showToast } from "../../../utils/toast";
import CreateFeatureFlagModal from "./CreateFeatureFlagModal";
import EditFeatureFlagModal from "./EditFeatureFlagModal";

const FeatureFlagsPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [filteredFlags, setFilteredFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [flagTools, setFlagTools] = useState<Record<string, ToolMapping[]>>({});
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null);

  const createModal = useDisclosure();
  const editModal = useDisclosure();
  const deleteDialog = useDisclosure();

  const loadFlags = async () => {
    try {
      setLoading(true);
      const data = await featureFlagService.getAllFlags();
      setFlags(data);
      setFilteredFlags(data);

      // Load tools for each flag
      const toolsMap: Record<string, ToolMapping[]> = {};
      await Promise.all(
        data.map(async (flag) => {
          try {
            const mappings = await featureFlagService.getMappingsByFlag(
              flag.key,
            );
            toolsMap[flag.key] = mappings;
          } catch (error) {
            console.error(`Error loading tools for flag ${flag.key}:`, error);
            toolsMap[flag.key] = [];
          }
        }),
      );
      setFlagTools(toolsMap);
    } catch (error: any) {
      showToast({
        title: "Error loading flags",
        description: error.message,
        status: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadFlags();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = flags.filter((flag) =>
        flag.name.toLowerCase().includes(searchTerm.toLowerCase()),
      );
      setFilteredFlags(filtered);
    } else {
      setFilteredFlags(flags);
    }
  }, [searchTerm, flags]);

  const handleCreate = () => {
    createModal.onOpen();
  };

  const handleEdit = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    editModal.onOpen();
  };

  const handleDeleteClick = (flag: FeatureFlag) => {
    setSelectedFlag(flag);
    deleteDialog.onOpen();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedFlag) return;

    try {
      await featureFlagService.deleteFlag(selectedFlag.id);
      showToast({
        title: "Policy deleted",
        description: `Access policy "${selectedFlag.name}" has been deleted`,
        status: "success",
      });
      deleteDialog.onClose();
      loadFlags();
    } catch (error: any) {
      showToast({
        title: "Error deleting flag",
        description: error.response?.data?.message || error.message,
        status: "error",
      });
    }
  };

  return (
    <Box p={6}>
      <VStack align="stretch" gap={6}>
        {/* Header */}
        <HStack justify="space-between">
          <Box>
            <Heading size="lg">{t("app.featureFlags.title")}</Heading>
            <Text color="gray.600" mt={1}>
              {t("app.featureFlags.subtitle")}
            </Text>
          </Box>
          <HStack>
            <AskStrikeUpButton
              context={t("app.askStrikeUp.context.featureFlags")}
            />
            <RefreshButton onClick={loadFlags} loading={loading} />
            <Button
              intent="secondary"
              onClick={() => navigate("/admin/feature-flags/preview")}
            >
              <FaEye /> {t("app.featureFlags.simulationTitle")}
            </Button>
            <Button intent="primary" onClick={handleCreate}>
              <FaPlus /> {t("app.featureFlags.createPolicy")}
            </Button>
          </HStack>
        </HStack>

        {/* Search */}
        <HStack>
          <Input
            placeholder={t("app.featureFlags.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            width="400px"
          />
          <IconButton aria-label={t("app.common.search")} variant="ghost">
            <FaSearch />
          </IconButton>
        </HStack>

        {/* Table */}
        <Box borderRadius="md" overflow="hidden" overflowX="auto">
          <Table.Root variant="outline" size="sm" minW="480px">
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>
                  {t("app.featureFlags.table.name")}
                </Table.ColumnHeader>
                <Table.ColumnHeader>
                  {t("app.featureFlags.table.tools")}
                </Table.ColumnHeader>
                <Table.ColumnHeader width="120px">
                  {t("app.featureFlags.table.actions")}
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {loading ? (
                <Table.Row>
                  <Table.Cell colSpan={3} textAlign="center">
                    <Text>{t("app.featureFlags.loading")}</Text>
                  </Table.Cell>
                </Table.Row>
              ) : filteredFlags.length === 0 ? (
                <Table.Row>
                  <Table.Cell colSpan={3} textAlign="center">
                    <Text color="gray.500">
                      {searchTerm
                        ? t("app.featureFlags.noMatchingPolicies")
                        : t("app.featureFlags.noPoliciesDescription")}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              ) : (
                filteredFlags.map((flag) => {
                  const mappings = flagTools[flag.key] || [];
                  const allTools = mappings.flatMap((m) => m.tools);
                  const displayTools = allTools.slice(0, 5);
                  const remainingCount = allTools.length - 5;

                  return (
                    <Table.Row key={flag.id}>
                      <Table.Cell>
                        <Text fontWeight="medium">{flag.name}</Text>
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap={2} wrap="wrap">
                          {displayTools.length === 0 ? (
                            <Text color="gray.400" fontSize="sm">
                              {t("app.featureFlags.noTools")}
                            </Text>
                          ) : (
                            <>
                              {displayTools.map((tool) => (
                                <Badge
                                  key={tool.toolId}
                                  colorPalette="blue"
                                  variant="subtle"
                                  size="sm"
                                >
                                  {tool.toolName}
                                </Badge>
                              ))}
                              {remainingCount > 0 && (
                                <Badge
                                  colorPalette="gray"
                                  variant="subtle"
                                  size="sm"
                                >
                                  {t("app.featureFlags.moreTools", {
                                    count: remainingCount,
                                  })}
                                </Badge>
                              )}
                            </>
                          )}
                        </HStack>
                      </Table.Cell>
                      <Table.Cell>
                        <HStack gap={1}>
                          <IconButton
                            aria-label={t("app.featureFlags.editFlag")}
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(flag)}
                          >
                            <FaEdit />
                          </IconButton>
                          <IconButton
                            aria-label={t("app.featureFlags.deleteFlag")}
                            size="sm"
                            variant="ghost"
                            colorPalette="red"
                            onClick={() => handleDeleteClick(flag)}
                          >
                            <FaTrash />
                          </IconButton>
                        </HStack>
                      </Table.Cell>
                    </Table.Row>
                  );
                })
              )}
            </Table.Body>
          </Table.Root>
        </Box>
      </VStack>

      {/* Create Modal */}
      <CreateFeatureFlagModal
        isOpen={createModal.open}
        onClose={createModal.onClose}
        onSuccess={loadFlags}
      />

      {/* Edit Modal */}
      {selectedFlag && (
        <EditFeatureFlagModal
          isOpen={editModal.open}
          onClose={editModal.onClose}
          flag={selectedFlag}
          onSuccess={loadFlags}
        />
      )}

      {/* Delete Confirmation */}
      <DeleteConfirmDialog
        isOpen={deleteDialog.open}
        onClose={deleteDialog.onClose}
        onConfirm={handleDeleteConfirm}
        title={t("app.featureFlags.deleteTitle")}
        message={t("app.featureFlags.deleteMessage", {
          name: selectedFlag?.name,
        })}
      />
    </Box>
  );
};

export default FeatureFlagsPage;
